"use client";
import { hasGoldenVisa } from "@/config/additionalSettings.config";
import { EnumModalName } from "@/enums/modal-state";
import { useIsGoldenVisaEligible } from "@/hooks/useIsGoldenVisaEligible";
import { useOrientation } from "@/hooks/useOrientation";
import IconArrow from "@/icons/ui/navigation/arrow.svg";
import CardImage from "@/images/visaCard.png";
import { useModalStore } from "@/store/modal.store";
import { cn } from "@/utils/cn";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useRef } from "react";
import Button from "../Common/Button";
import styles from "./styles.module.css";

interface Props {
  children: React.ReactNode;
}

const GoldenVisaMainHero: React.FC<Props> = (props) => {
  const { openModal } = useModalStore();
  const params = useSearchParams();
  const orientation = useOrientation();
  const isVisa = useIsGoldenVisaEligible();
  const t = useTranslations("visa");
  const headerRef = useRef(null);

  useEffect(() => {
    if (isVisa) {
      headerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isVisa]);

  if (!params.get("isGoldenVisaEligible") || !hasGoldenVisa()) {
    return props.children;
  }

  return (
    <div
      className={cn(
        styles.goldenBackground,
        orientation === "landscape" && "md:min-h-[100vh]",
        orientation === "portrait" && "max-md:min-h-[100vh]",
        "px-5 md:px-8 xl:px-32 md:bg-left",
        "justify-center",
        // "md:max-h-[100vh]",
      )}
      ref={headerRef}
    >
      <div className="absolute top-0 left-0 right-0">{props.children}</div>
      <div
        className={cn(
          styles.heroContainer,
          "pt-64 pb-6 md:pb-8 xl:pb-11 max-w-screen-3xl",
        )}
      >
        <div />
        <div className="flex items-start justify-between gap-4 w-full">
          <div className="w-full">
            <div className="flex flex-col md:flex-row items-start md:justify-between mb-3 sm:mb-9 xl:mb-0 w-full">
              <div className={styles.heroGlass}>
                <div className={styles.heroGlass__blur} />
                <div className={styles.heroGlass__content}>
                  <h1
                    className={cn(
                      "text-white font-medium leading-[1.1] text-[35px] md:text-[45px] md:leading-1 xl:text-[55px] xl:leading-[1.1] mb-1 md:mb-14 xl:mb-[22px]",
                      styles.textShadowSoft,
                    )}
                  >
                    {t("HeaderHeroTitle")}{" "}
                    <span
                      className={cn(
                        styles.accentTitle,
                        "inline-block break-words",
                      )}
                    >
                      {t("HeaderHeroTitleAccent")}
                    </span>
                  </h1>
                  <h2
                    className={cn(
                      "font-normal text-white max-w-full md:max-w-[402px] xl:max-w-[680px] text-[18px] leading-[1.5] mb-[18px] md:mb-[38px] xl:text-[21px]",
                      styles.textShadowStrong,
                    )}
                  >
                    {t("HeaderHeroSubTitle1")}{" "}
                    <b>{t("HeaderHeroSubTitleAccent")}</b>{" "}
                    {t("HeaderHeroSubTitle2")}
                  </h2>
                </div>
              </div>
              <Image
                src={CardImage}
                alt="Visa"
                width={296.04}
                height={207.66}
                className="xl:hidden w-[179.39px] md:w-[296.04px]"
              />
            </div>
            <div className="flex flex-col md:flex-row justify-start gap-3 md:gap-7">
              <div>
                <Button
                  type="white"
                  className="h-[54px] md:min-w-[260px]"
                  onClick={() => openModal(EnumModalName.callBack)}
                >
                  {t("HeaderHeroContact")}
                </Button>
              </div>
              <div>
                <Button
                  type="white"
                  className="h-[54px]  md:min-w-[260px]"
                  link="/golden-visa/"
                >
                  {t("HeaderHeroSeeMore")}
                </Button>
              </div>
            </div>
          </div>
          <Image
            src={CardImage}
            alt="Visa"
            width={553}
            height={338}
            className="hidden xl:block"
          />
        </div>
        <Link
          className="text-white text-lg font-medium flex items-center gap-[1rem]"
          href={"#search"}
        >
          <span className={cn(styles.linkShadowStrong, styles.textShadowSoft)}>
            {t("HeaderHeroGoToProperties")}
          </span>
          <IconArrow
            width={"16"}
            height={"16"}
            className={cn("text-white -rotate-90", styles.arrowBounce)}
          />
        </Link>
      </div>
    </div>
  );
};

export default GoldenVisaMainHero;
